// Planque : modules à améliorer (crédits + objets ramenés de raid), fabrication en temps réel, niveau du récupérateur.
// Ces règles servent au serveur (vérification) et au client (affichage).

export type ModuleId = 'generator' | 'workbench' | 'medstation' | 'storage' | 'tailor' | 'radio' | 'armory' | 'rest';

export interface ModuleLevel {
  credits: number;
  items: [string, number][]; // objets pris dans le coffre
  needs?: Partial<Record<ModuleId, number>>; // autres modules requis (niveau minimal)
  effect: string;
}
export interface ModuleDef { id: ModuleId; name: string; bio: string; levels: ModuleLevel[] }

export const MODULES: ModuleDef[] = [
  {
    id: 'generator', name: 'Générateur', bio: 'Un groupe électrogène récupéré. Sans courant, les ateliers ne tournent pas.',
    levels: [
      { credits: 6000, items: [['l_fuel', 1], ['l_cable', 1]], effect: 'Alimente les ateliers : la fabrication devient possible.' },
      { credits: 22000, items: [['l_fuel', 2], ['l_board', 1], ['l_cable', 2]], needs: { workbench: 1 }, effect: 'Fabrication 30 % plus rapide.' },
    ],
  },
  {
    id: 'workbench', name: 'Établi', bio: 'De quoi recharger des douilles, réparer, bricoler.',
    levels: [
      { credits: 4000, items: [['l_toolbox', 1]], effect: 'Cartouches 9 mm, attelles.' },
      { credits: 15000, items: [['l_cable', 2], ['l_board', 1]], needs: { generator: 1 }, effect: 'Cartouches de 12, munitions 5,56 perforantes.' },
      { credits: 40000, items: [['l_toolbox', 2], ['l_drive', 2]], needs: { generator: 2 }, effect: 'Munitions 7,62 perforantes, gilets de police.' },
    ],
  },
  {
    id: 'medstation', name: 'Infirmerie', bio: 'Une table, une lampe, des étagères. Assez pour préparer des soins.',
    levels: [
      { credits: 3000, items: [['l_meds', 2]], effect: 'Bandages.' },
      { credits: 16000, items: [['l_meds', 3], ['m_kit', 1]], needs: { generator: 1 }, effect: 'Trousses de soins.' },
    ],
  },
  {
    id: 'storage', name: 'Réserve', bio: 'Des étagères en plus pour le coffre.',
    levels: [
      { credits: 9000, items: [['l_cable', 1]], effect: 'Coffre agrandi : 10 × 30 cases.' },
      { credits: 28000, items: [['l_toolbox', 1], ['l_cable', 2]], needs: { workbench: 1 }, effect: 'Coffre agrandi : 10 × 36 cases.' },
      { credits: 75000, items: [['l_gold', 1], ['l_toolbox', 1]], needs: { workbench: 2 }, effect: 'Coffre agrandi : 10 × 44 cases.' },
    ],
  },
  {
    id: 'tailor', name: 'Atelier de couture', bio: 'Pour coudre une poche sécurisée plus grande dans la doublure.',
    levels: [
      { credits: 12000, items: [['l_radio', 1], ['l_watch', 1]], effect: 'Poche sécurisée 2 × 2.' },
      { credits: 45000, items: [['l_gold', 1], ['l_binoc', 1]], needs: { workbench: 2 }, effect: 'Poche sécurisée 3 × 2.' },
    ],
  },
  {
    id: 'radio', name: 'Poste radio', bio: 'On écoute les marchands, on négocie mieux.',
    levels: [
      { credits: 14000, items: [['l_radio', 2]], needs: { generator: 1 }, effect: 'Reventes 10 % plus chères.' },
      { credits: 48000, items: [['l_radio', 1], ['l_drive', 2], ['l_board', 2]], needs: { generator: 2 }, effect: 'Achats 10 % moins chers.' },
    ],
  },
  {
    id: 'armory', name: 'Armurerie', bio: 'Un établi à part pour monter des armes complètes à partir de pièces récupérées.',
    levels: [
      { credits: 18000, items: [['l_toolbox', 1], ['l_cable', 2]], needs: { workbench: 2 }, effect: 'Fabrication : pistolets 9 mm, fusils à pompe.' },
      { credits: 60000, items: [['l_toolbox', 2], ['l_board', 2], ['l_drive', 1]], needs: { generator: 2, workbench: 3 }, effect: 'Fabrication : fusils d’assaut, fusils à lunette.' },
    ],
  },
  {
    id: 'rest', name: 'Zone de repos', bio: 'Un canapé, un lit de camp, une lampe. On dort mieux, on apprend plus de chaque sortie.',
    levels: [
      { credits: 5000, items: [['l_watch', 1]], effect: '+10 % d’expérience à chaque raid.' },
      { credits: 25000, items: [['l_radio', 1], ['l_gold', 1]], needs: { generator: 1 }, effect: '+20 % d’expérience à chaque raid.' },
    ],
  },
];
export const moduleDef = (id: string) => MODULES.find(m => m.id === id);

export interface Recipe { id: string; module: ModuleId; level: number; name: string; inputs: [string, number][]; output: [string, number]; time: number } // time en secondes
export const RECIPES: Recipe[] = [
  { id: 'r_9', module: 'workbench', level: 1, name: 'Cartouches 9 mm', inputs: [['l_cable', 1]], output: ['a_9', 40], time: 180 },
  { id: 'r_splint', module: 'workbench', level: 1, name: 'Attelle', inputs: [['m_bandage', 1], ['l_cable', 1]], output: ['m_splint', 1], time: 120 },
  { id: 'r_12', module: 'workbench', level: 2, name: 'Cartouches de 12', inputs: [['l_cable', 1], ['l_fuel', 1]], output: ['a_12', 24], time: 300 },
  { id: 'r_556ap', module: 'workbench', level: 2, name: '5,56 perforantes', inputs: [['a_556', 30], ['l_toolbox', 1]], output: ['a_556ap', 30], time: 480 },
  { id: 'r_762ap', module: 'workbench', level: 3, name: '7,62 perforantes', inputs: [['a_762', 20], ['l_toolbox', 1], ['l_board', 1]], output: ['a_762ap', 20], time: 600 },
  { id: 'r_police', module: 'workbench', level: 3, name: 'Gilet de police', inputs: [['v_soft', 1], ['l_toolbox', 1], ['l_cable', 2]], output: ['v_police', 1], time: 900 },
  { id: 'r_pistol', module: 'armory', level: 1, name: 'Pistolet 9 mm', inputs: [['l_toolbox', 1], ['l_cable', 1]], output: ['w_pistol', 1], time: 600 },
  { id: 'r_sg', module: 'armory', level: 1, name: 'Fusil à pompe', inputs: [['l_toolbox', 1], ['l_fuel', 1], ['l_cable', 1]], output: ['w_sg', 1], time: 900 },
  { id: 'r_smg', module: 'armory', level: 1, name: 'Pistolet-mitrailleur', inputs: [['l_toolbox', 1], ['l_cable', 2]], output: ['w_smg', 1], time: 1200 },
  { id: 'r_supp', module: 'armory', level: 2, name: 'Silencieux', inputs: [['l_toolbox', 1], ['l_board', 1]], output: ['mz_supp', 1], time: 900 },
  { id: 'r_ar', module: 'armory', level: 2, name: 'Fusil d’assaut 5,56', inputs: [['l_toolbox', 2], ['l_board', 1], ['a_556', 30]], output: ['w_ar', 1], time: 1500 },
  { id: 'r_dmr', module: 'armory', level: 2, name: 'Fusil à lunette 7,62', inputs: [['l_toolbox', 2], ['l_board', 2], ['l_binoc', 1]], output: ['w_dmr', 1], time: 1800 },
  { id: 'r_bandage', module: 'medstation', level: 1, name: 'Bandages', inputs: [['l_meds', 1]], output: ['m_bandage', 3], time: 120 },
  { id: 'r_kit', module: 'medstation', level: 2, name: 'Trousse de soins', inputs: [['l_meds', 2], ['m_bandage', 2]], output: ['m_kit', 1], time: 360 },
];
export const recipeById = (id: string) => RECIPES.find(r => r.id === id);

export interface HideoutState {
  levels: Partial<Record<ModuleId, number>>;
  crafts: Partial<Record<ModuleId, { recipe: string; ends: number }>>; // ends : horodatage serveur (ms)
}
export const newHideout = (): HideoutState => ({ levels: {}, crafts: {} });
export const levelOf = (h: HideoutState, id: ModuleId) => h.levels[id] ?? 0;

// Effets des modules
export const STASH_ROWS = [24, 30, 36, 44];
export const POUCH_SIZE: [number, number][] = [[2, 1], [2, 2], [3, 2]];
export const craftTime = (h: HideoutState, r: Recipe) => Math.round(r.time * (levelOf(h, 'generator') >= 2 ? 0.7 : 1));
export const sellBonus = (h: HideoutState) => (levelOf(h, 'radio') >= 1 ? 1.1 : 1);
// Réparation d'une protection : la moitié de sa valeur pour la remettre à neuf, au prorata de l'usure ;
// l'atelier de couture fait le travail 30 % moins cher. max : durabilité maximale actuelle, base : celle d'origine.
export function repairCost(h: HideoutState, dur: number, max: number, base: number, value: number) {
  const missing = Math.max(0, max - dur);
  return Math.round((value * 0.5 * missing) / base * (levelOf(h, 'tailor') >= 1 ? 0.7 : 1));
}
// Chaque réparation fait perdre pour de bon 10 % de ce qui est réparé (jamais sous 30 % de la durabilité d'origine)
export const REPAIR_WEAR = 0.1;
export const repairedMax = (dur: number, max: number, base: number) => Math.max(base * 0.3, Math.round((max - (max - dur) * REPAIR_WEAR) * 10) / 10);
export const xpBonus = (h: HideoutState) => 1 + 0.1 * levelOf(h, 'rest');
export const buyDiscount = (h: HideoutState) => (levelOf(h, 'radio') >= 2 ? 0.9 : 1);

// ---------------------------------------------------------------- Niveau du récupérateur

// Expérience nécessaire pour atteindre le niveau l (niveau 1 au départ)
export const xpFor = (l: number) => 250 * l * (l - 1);
export const MAX_LEVEL = 15;
export function levelFromXp(xp: number) { let l = 1; while (l < MAX_LEVEL && xp >= xpFor(l + 1)) l++; return l; }
export const XP = { infected: 15, bandit: 60, boss: 400, searched: 8, extracted: 150, survivedPerMin: 10 };
